<template>
  <div>
    <div class="mb-4">
      <label class="uppercase font-semibold text-sm">To (Customer Email)</label>
      <input
          v-model="recipientEmail"
          class="w-full bge-input bge-input-spacing rounded"
          type="email"
          placeholder="Recipient email..."
      />
    </div>

    <div class="mb-4">
      <label class="uppercase font-semibold text-sm">Subject</label>
      <input
          v-model="emailSubject"
          class="w-full bge-input bge-input-spacing rounded"
          type="text"
      />
    </div>

    <div class="mb-4">
      <label class="uppercase font-semibold text-sm">Message</label>
      <StyledTextEditor v-model="emailMessage" :options="editorOptions" />
    </div>

    <div class="mb-6 border rounded p-3">
      <label class="uppercase font-semibold text-sm">Attachments</label>

      <div class="flex items-center gap-2 mt-2">
        <Checkbox v-model="attachJobSheet" :binary="true" inputId="attach-job-sheet" />
        <label for="attach-job-sheet" class="font-medium cursor-pointer">
          {{ jobSheet.job_sheet_no }} - Job Sheet.pdf
        </label>
      </div>

      <template v-if="certificateState.length > 0">
        <div class="mt-3 text-sm font-semibold text-secondary">Certificates for this job sheet</div>
        <div v-for="cert in certificateState" :key="cert.certificate_id" class="flex items-center gap-2 mt-1">
          <Checkbox v-model="cert.selected" :binary="true" :inputId="`cert-${cert.certificate_id}`" />
          <label :for="`cert-${cert.certificate_id}`" class="cursor-pointer">
            {{ cert.name }}
            <span v-if="cert.status !== 'completed'" class="text-xs text-gray-500">({{ cert.status }})</span>
          </label>
        </div>
      </template>
    </div>

    <div class="flex justify-end">
      <AppButton color="blue" label="Send Email" @click="sendEmail" :icon="true">
        <template v-slot:icon>
          <Icon iconType="mail" class="mr-2" />
        </template>
      </AppButton>
    </div>

    <div class="mt-10">
      <EmailQueueTable ref="emailQueueTable" :referenceId="jobSheet.job_sheet_id"></EmailQueueTable>
    </div>
  </div>
</template>

<script>
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import StyledTextEditor from "@/components/StyledTextEditor.vue";
import notificationMixin from "@/mixins/notificationMixin";
import EmailQueueTable from "@/views/HR/Sales/Components/EmailQueueTable.vue";

export default {
  name: "SendJobSheetEmail",
  mixins: [notificationMixin],
  components: {
    Icon,
    AppButton,
    EmailQueueTable,
    StyledTextEditor,
  },
  props: {
    jobSheet: { type: Object, required: true },
  },
  data() {
    const customer = this.jobSheet.customer || {};
    const engineer = this.jobSheet.engineer || {};
    const customerName = customer.name || "";
    const engineerName = engineer.name || "";

    const certificateState = Array.isArray(this.jobSheet.certificates)
        ? this.jobSheet.certificates
            .filter((cert) => cert.certificate_id)
            .map((cert) => ({
              ...cert,
              selected: false,
              url: `certificate/${cert.certificate_id}/report/${(cert.name || "").toLowerCase()}`,
              fileName: `${cert.name}-${cert.certificate_id}.pdf`,
            }))
        : [];

    return {
      recipientEmail: "",
      emailSubject: `Your Job Sheet ${this.jobSheet.job_sheet_no}`,
      emailMessage:
          `Dear ${customerName},\n\nPlease find attached your job sheet.\n\nKind regards,\n${engineerName}`,
      editorOptions: {
        toolbar: [
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
        ],
      },
      customerName,
      engineerName,
      attachJobSheet: true,
      certificateState,
      defaultSubject: "",
      defaultEmailText: "",
    };
  },
  mounted() {
    this.fetchEmailTemplate();
    this.fetchCustomerEmail();
  },
  methods: {
    replacePlaceholders(template, data) {
      return template.replace(/{{\s*(\w+)\s*}}/g, (match, p1) => data[p1] || "");
    },
    async fetchEmailTemplate() {
      try {
        const template = await this.EmailService.getTemplateByKey("schedule_job_sheet_email_temp");
        this.defaultSubject = this.replacePlaceholders(template.subject || "", {
          jobId: this.jobSheet.job_sheet_no || "",
        });
        this.defaultEmailText = this.replacePlaceholders(template.body || "", {
          customer: this.customerName,
          jobSheetNumber: this.jobSheet.job_sheet_no || "",
          jobDate: this.jobDate(),
          engineerName: this.engineerName,
        });

        this.emailSubject = this.defaultSubject;
        this.emailMessage = this.defaultEmailText;
      } catch (error) {
        console.error("Error fetching email template:", error);
      }
    },
    async fetchCustomerEmail() {
      const customer = this.jobSheet.customer || {};
      if (!customer.contact_id) return;

      try {
        const contact = await this.ContactService.getContactById(customer.contact_id);
        const people = Array.isArray(contact.people) ? contact.people : [];
        const person = people.find((p) => p.is_default) || people[0];
        if (person && person.email) {
          this.recipientEmail = person.email;
        }
      } catch (error) {
        console.error("Error fetching customer contact:", error);
      }
    },
    jobDate() {
      return this.jobSheet.job_start_date
          ? this.$moment(this.jobSheet.job_start_date).format("DD/MM/YYYY")
          : "";
    },
    async sendEmail() {
      if (!this.recipientEmail) {
        this.notifyError("Recipient email is required");
        return;
      }

      const attachments = [];
      if (this.attachJobSheet) {
        attachments.push({
          url: `job-sheets/${this.jobSheet.job_sheet_id}/report/job-sheet`,
          file_name: `${this.jobSheet.job_sheet_no}-Job-Sheet.pdf`,
        });
      }
      this.certificateState.forEach((cert) => {
        if (cert.selected) {
          attachments.push({ url: cert.url, file_name: cert.fileName });
        }
      });

      if (attachments.length === 0) {
        this.notifyError("Select at least one attachment");
        return;
      }

      const emailData = {
        template_key: "schedule_job_sheet_email_temp",
        reference_id: this.jobSheet.job_sheet_id,
        to: [{ name: this.customerName, email: this.recipientEmail }],
        cc: [],
        bcc: [],
        category: "notification",
        status: "PENDING",
        data: {
          customer: this.customerName,
          jobDate: this.jobDate(),
          engineerName: this.engineerName,
          jobId: this.jobSheet.job_sheet_no,
        },
        use_custom_subject_body: true,
        custom_subject: this.emailSubject,
        custom_body: this.emailMessage,
        has_attachments: true,
        attachments,
      };

      try {
        await this.EmailService.createQueueItem(emailData);
        this.notifySuccess("Job Sheet Email queued successfully");
        await this.$refs.emailQueueTable.loadEmailQueues();
      } catch (error) {
        console.error("Error sending job sheet email:", error);
        this.notifyError("Failed to send job sheet email");
      }
    },
  },
};
</script>
